import { getPhoneData } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { type TriggerRef } from '@rn-primitives/popover'
import { AsYouType, type CountryCode } from 'libphonenumber-js'
import { useCallback, useMemo, useRef, useState } from 'react'
import { type BlurEvent, type FocusEvent, Pressable } from 'react-native'
import { PatternFormat } from 'react-number-format'
import Icon from '~/components/Icon'
import { ScrollView } from '~/components/ScrollView'
import { Button } from '~/components/ui/button'
import { Input, type InputProps, type InputVariant } from '~/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'
import { countries } from '~/locales/countries'

type PhoneInputProps = {
  value: string | undefined
  disabled?: boolean
  defaultCountryCode?: CountryCode
  onChange: (
    displayPhoneNumber: string,
    countryCode: CountryCode,
    storedPhoneNumber: string,
    isValid: boolean
  ) => void
  className?: string
  variant?: InputVariant
  countrySelectClassName?: string
} & Omit<InputProps, 'type' | 'defaultValue' | 'onChange' | 'onChangeText' | 'suffix'>

const FALLBACK_COUNTRY = 'FI'

export function PhoneInputField({
  value: valueProp,
  defaultCountryCode = FALLBACK_COUNTRY,
  onChange,
  className,
  countrySelectClassName,
  variant = 'line',
  ...inputProps
}: PhoneInputProps) {
  const [countryCode, setCountryCode] = useState<CountryCode>(defaultCountryCode)
  const { t } = useTranslations('common')

  const selectedCountry = useMemo(
    () => countries.find((country) => country.iso2 === countryCode),
    [countryCode]
  )

  const createFormatter = useCallback(
    (inputValue: string) => {
      const formatter = new AsYouType((selectedCountry?.iso2 || defaultCountryCode) as CountryCode)

      formatter.input(inputValue)
      return formatter
    },
    [defaultCountryCode, selectedCountry?.iso2]
  )

  const numberValue = useMemo(
    () => createFormatter(valueProp || '').getNationalNumber(),
    [createFormatter, valueProp]
  )

  const formatMask = useMemo(() => {
    const valueLength = numberValue.trim().length || 0
    const patternExtensionLength = Math.max(0, Math.min(valueLength - 9, 6)) + 1
    // eslint-disable-next-line i18next/no-literal-string
    return `+${selectedCountry?.phone_code} xx xxxx xxx ${'x'.repeat(patternExtensionLength)}`
  }, [selectedCountry?.phone_code, numberValue])

  const [countrySearch, setCountrySearch] = useState('')

  const countryOptions = useMemo(() => {
    const filtered = !countrySearch
      ? countries
      : countries.filter(
          (country) =>
            country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
            country.phone_code.toString().startsWith(countrySearch) ||
            country.iso2.toLowerCase().startsWith(countrySearch.toLowerCase())
        )

    return filtered.sort((a, b) => {
      // Finland first
      if (a.iso2 === 'FI') return -1
      if (b.iso2 === 'FI') return 1

      // Nordic countries (subregion_id 18) next
      const aIsNordic = a.subregion_id === '18'
      const bIsNordic = b.subregion_id === '18'
      if (aIsNordic && !bIsNordic) return -1
      if (!aIsNordic && bIsNordic) return 1

      // European countries (region_id 4) next
      const aIsEuropean = a.region_id === '4'
      const bIsEuropean = b.region_id === '4'
      if (aIsEuropean && !bIsEuropean) return -1
      if (!aIsEuropean && bIsEuropean) return 1

      // Within same priority group, sort by id
      return a.id - b.id
    })
  }, [countrySearch])

  const countryPopoverTriggerRef = useRef<TriggerRef>(null)

  return (
    <>
      <Popover>
        <PopoverTrigger asChild ref={countryPopoverTriggerRef}>
          <Button
            variant="ghost"
            size="xs"
            className={cn(
              '-ml-3 justify-between whitespace-nowrap px-4 pr-3',
              variant === 'line' && 'border-0 bg-transparent text-primary caret-accent ring-0',
              countrySelectClassName
            )}>
            {selectedCountry?.name ? (
              <Text className="-mb-px text-primary">{selectedCountry.emoji}</Text>
            ) : (
              t(($) => $.phoneInput.selectCountry)
            )}
            <Icon name="chevron-expand-sharp" className="ml-1 block size-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-max p-0"
          align="start"
          sideOffset={5}
          avoidCollisions
          sticky="partial">
          <Input
            value={countrySearch}
            onChangeText={setCountrySearch}
            placeholder={t(($) => $.phoneInput.searchCountry)}
            variant={variant}
            className="mx-3 mt-1"
          />
          <ScrollView
            className="max-h-[250px] min-h-[150px] w-max min-w-80"
            contentContainerClassName="gap-0.5 px-3 pt-3">
            {countryOptions.map((country) => {
              const TextItem = ({
                children,
                className,
              }: {
                children: React.ReactNode
                className?: string
              }) => (
                <Text
                  className={cn(
                    'text-primary group-hover/button:text-accent',
                    countryCode === country.iso2 && 'text-accent',
                    className
                  )}>
                  {children}
                </Text>
              )

              return (
                <Pressable
                  key={country.iso3}
                  onPress={() => {
                    setCountryCode(country.iso2 as CountryCode)
                    countryPopoverTriggerRef.current?.close()
                  }}
                  className={cn(
                    'group/button flex flex-row items-center gap-2 rounded-md px-4 py-1.5 transition-transform duration-150 hover:bg-primary/20 active:scale-[0.97]',
                    countryCode === country.iso2 && 'bg-primary text-background hover:bg-primary/90'
                  )}>
                  <TextItem className={cn('flex flex-row items-center gap-2')}>
                    <Icon
                      name="check"
                      className={cn(
                        'size-4',
                        countryCode === country.iso2 ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <TextItem>{country.emoji}</TextItem>
                    <TextItem>{country.name}</TextItem>
                    <TextItem>(+{country.phone_code})</TextItem>
                  </TextItem>
                </Pressable>
              )
            })}
          </ScrollView>
        </PopoverContent>
      </Popover>
      <PatternFormat
        data-testid={inputProps.testID}
        type="tel"
        placeholder={inputProps.placeholder}
        value={numberValue}
        autoFocus={inputProps.autoFocus}
        format={formatMask}
        onFocus={(evt) => inputProps.onFocus?.(evt as unknown as FocusEvent)}
        onBlur={(evt) => inputProps.onBlur?.(evt as unknown as BlurEvent)}
        patternChar="x"
        onPaste={(event) => {
          // eslint-disable-next-line i18next/no-literal-string
          const pastedText = event.clipboardData.getData('text').trim()
          const phoneNumber = getPhoneData(pastedText, countryCode)

          if (phoneNumber && phoneNumber.isPossible) {
            event.preventDefault()
            event.stopPropagation()

            onChange(pastedText, countryCode, phoneNumber.phoneNumber ?? '', true)
          }
        }}
        onValueChange={(value) => {
          const trimmedValue = (value.formattedValue || '').trim()
          const phoneNumber = getPhoneData(trimmedValue, countryCode)

          onChange(
            trimmedValue,
            countryCode,
            phoneNumber?.phoneNumber ?? '',
            phoneNumber?.isPossible ?? false
          )
        }}
        className={cn(className)}
      />
    </>
  )
}
